import React, {
  useState,
  useEffect,
  useContext,
  useCallback,
  useRef,
} from "react";
import { NotificationManager } from "react-notifications";
import { useParams, Link } from "react-router-dom";
import SideLabelInput from "../Components/SideLabelInput";
import SmallInput from "../Components/SmallInput";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowLeft } from "@fortawesome/free-solid-svg-icons";
import useApi from "../Api/Api";
import Select from "react-select";
import SalesLayout from "./SalesLayout";
import Button from "../Components/Button";
import { FilterContext } from "../FilterContextProvider";
import useAllProducts from "../hooks/useAllProducts";
import StatesList from "../Components/StatesList";
import ReactToPrint from "react-to-print";
import Footer from "../Components/Footer";

const CompDetails = () => {
  const api = useApi();
  const [showButtons, setShowButtons] = useState(false);
  const [errorMessage, setErrorMessage] = useState(false);
  const [isFetching, setIsFetching] = useState(false);
  const params = useParams();
  const { filterData } = useContext(FilterContext);
  const { allProducts } = useAllProducts(true);
  const [event, setEvent] = useState({});
  const [sales, setSales] = useState([]);
  const [alterProdsObj, setAlterProdsObj] = useState({});
  const printRef = useRef();

  sales.forEach((sale) => {
    const prod = allProducts.find(({ id }) => id === sale.product_id);
    if (prod?.image_url) {
      sale.image = prod?.image_url;
    } else {
      sale.image = "";
    }
  });

  allProducts.forEach((product) => {
    product.product_size = product.size?.name || product.size_other;
  });

  const productNames = [
    ...new Set(
      sales.map(
        (sale) => `${sale.product_name}*${sale.category?.index}*%${sale.image}`
      )
    ),
  ];

  const productSizes = [
    ...new Set(sales.map((sale) => `${sale.product_size}*${sale.size?.index}`)),
  ];

  productNames
    .sort((a, b) => a.split("*")[0].localeCompare(b.split("*")[0]))
    .sort((a, b) => a.split("*")[1].localeCompare(b.split("*")[1]));

  productSizes.sort((a, b) => a.split("*")[1].localeCompare(b.split("*")[1]));

  const prodNamesArray = productNames.map(function (value) {
    return {
      name: value.split("*")[0],
      category_index: value.split("*")[1],
      image: value.split("%")[1],
    };
  });
  const { renderFooter, splitItemsList } = Footer(prodNamesArray);

  const prodSizesArray = productSizes.map(function (value) {
    return {
      name: value.split("*")[0],
      size_index: value.split("*")[1],
    };
  });

  const handleSelect = (option, name) => {
    if (name.name === "party") {
      setAlterProdsObj({});
      setEvent({
        ...event,
        [name.name]: option.value,
        venue_rep_name: option.label,
      });
    } else {
      setEvent({
        ...event,
        [name.name]: option.value,
      });
    }
  };

  const stateOptions = StatesList.map((state) => ({
    label: state.name,
    value: state.abbreviation,
  }));

  const handleEventChange = ({ target: { id, value } }) => {
    setEvent({
      ...event,
      [id]: value,
    });
    setShowButtons(true);
  };

  const handleInputChange = ({ target: { name, value } }, productToRender) => {
    const prodToAlter = allProducts.find((one) =>
      name !== "null"
        ? one.name === productToRender.product_name && one.product_size === name
        : one.name === productToRender.product_name
    );
    setAlterProdsObj({
      ...alterProdsObj,
      [productToRender.id]: {
        ...prodToAlter,
        id: productToRender.id,
        [event.party]: value,
        product_id: prodToAlter.id,
        product_name: prodToAlter.name,
        product_category: prodToAlter.category.name,
        date: event.date,
      },
    });
  };

  const getEvent = useCallback(() => {
    api.get(`/events/${params.eventId}`).then((r) => {
      setEvent(r.data);
    });
  }, [api, setEvent, params.eventId]);

  const getSales = useCallback(() => {
    api
      .get(
        `/sales/all?artist_id=${filterData.artist_id}&event_id=${params.eventId}`
      )
      .then((r) => {
        setSales(r.data);
      });
  }, [api, filterData.artist_id, params.eventId]);

  useEffect(() => {
    getEvent();
  }, [getEvent]);

  useEffect(() => {
    getSales();
  }, [getSales]);

  const revert = () => {
    let message =
      "Are you sure you want to revert? All unsaved changes will be lost.";
    const check = window.confirm(message);
    if (check) {
      setAlterProdsObj({});
      getEvent();
      getSales();
      setShowButtons(false);
      setErrorMessage(false);
    }
  };

  useEffect(() => {
    if (sales[0]?.band_comp_quantity !== 0) {
      setEvent({ ...event, party: "band_comp_quantity", whom: "Band Comp" });
    } else if (sales[0]?.management_comp !== 0) {
      setEvent({ ...event, party: "management_comp", whom: "Management Comp" });
    } else if (sales[0]?.ctm_comp !== 0) {
      setEvent({ ...event, party: "ctm_comp", whom: "TM Comp" });
    }
  }, [sales]); // eslint-disable-line

  const handleUpdateSales = (e) => {
    setIsFetching(true);
    e.preventDefault();
    api
      .put(`/events/${event.id}`, {
        event: {
          ...event,
          comment: `$${grossAll}`,
          sales_attributes: Object.values(alterProdsObj).map((each) => each),
        },
      })
      .then(() => {
        NotificationManager.success(
          "Sales successfully updated",
          "Success",
          2000
        );
        setAlterProdsObj({});
        setErrorMessage(false);
        getEvent();
        getSales();
      })
      .catch((error) => {
        setErrorMessage(error.response.data);
      })
      .finally(() => {
        setIsFetching(false);
      });
  };

  sales.forEach((sale) => {
    const saleToAddFrom = alterProdsObj[sale.id] || sale;
    sale.total = parseInt(saleToAddFrom[event.party]) * saleToAddFrom.cost;
  });

  const grossAll = sales.reduce((a, b) => {
    return a + b.total;
  }, 0);

  const renderTable = () => {
    return (
      <table className="w-full">
        <thead className="text-xs font-light">
          <tr className="text-left border-b-2 h-11 text-center">
            <td className="w-1/4 text-left pl-6">PRODUCT</td>
            <td className="w-1/8 text-left pl-3">IMAGE</td>
            {prodSizesArray.map((prodSize) => {
              return (
                <td key={prodSize.name} className="w-thinner">
                  {prodSize.name !== "null" ? prodSize.name : "No size"}
                </td>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {splitItemsList.map((prod, i) => {
            const prodName =
              Object.values(alterProdsObj).find(
                (one) => one.name === prod.name
              ) || prod;
            return (
              <tr
                key={prodName.name}
                className={
                  Object.values(alterProdsObj).find(
                    (one) => one.name === prodName.name
                  )
                    ? "bg-yellow-50 border-t text-xs h-11 text-center"
                    : "border-t text-xs h-11 text-center"
                }
              >
                <td className="pl-6 text-left text-sm">{prodName.name}</td>
                <td className="text-center">
                  {prodName.image && (
                    <img
                      src={prodName.image}
                      alt="Product"
                      className="h-16 rounded-md"
                    />
                  )}
                </td>
                {prodSizesArray.map((prodSize) => {
                  const product =
                    Object.values(alterProdsObj).find((one) =>
                      prodSize.name !== "null"
                        ? one.name === prodName.name &&
                          one.product_size === prodSize.name
                        : one.name === prodName.name &&
                          one.product_size === null
                    ) ||
                    sales.find((sale) =>
                      sale.product_size !== null
                        ? sale.product_size === prodSize.name &&
                          sale.product_name === prod.name
                        : prodSize.name === "null" &&
                          sale.product_name === prod.name
                    ) ||
                    prod;
                  return (
                    <td key={prodSize.name}>
                      <SmallInput
                        name={prodSize.name}
                        id={`${prodSize}_${i}`}
                        onChange={(e) => handleInputChange(e, product)}
                        value={product?.[event.party] ?? ""}
                        disabled={
                          prodSize.name !== "null"
                            ? sales.find(
                                (one) =>
                                  one.name === product.product_name &&
                                  one.product_size === prodSize.name
                              )
                            : sales.find(
                                (one) =>
                                  one.name === product.product_name &&
                                  one.product_size === null
                              )
                        }
                      />
                    </td>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
      </table>
    );
  };

  return (
    <SalesLayout>
      <div className="mt-10 mx-8 py-5 text-black bg-white rounded-lg border">
        <div className="font-medium mx-8 text-black text-sm">
          <Link to="/sales/comps/list">
            <FontAwesomeIcon icon={faArrowLeft}></FontAwesomeIcon> Back to comps
            list
          </Link>
        </div>
        <div className="py-2 mx-8 text-2xl font-medium">Comp Details</div>
        <div className="flex justify-end text-sm -mt-9 mr-8">
          <ReactToPrint
            trigger={() => <Button className="button" text="Print"></Button>}
            content={() => printRef.current}
          />
        </div>
        <div ref={printRef}>
          <div className="my-4 text-sm">
            <div className="flex flex-col mx-8">
              <div className="flex mb-4">
                <div className="w-1/4 mr-8">
                  <div className="flex">
                    <div className="flex w-1/3 items-center">
                      <label htmlFor="date" className="text-xs">
                        Date
                      </label>
                    </div>
                    <input
                      value={event.date?.split("T")[0] ?? ""}
                      type="date"
                      id="date"
                      name="date"
                      className="text-xs h-9 pl-2 ml-5 font-light rounded-md border w-full justify-self-end"
                      onChange={handleEventChange}
                      required
                    ></input>
                  </div>
                  {errorMessage?.errors?.date && (
                    <div className="text-red-500 pb-2">
                      {errorMessage.errors.date}
                    </div>
                  )}
                </div>
                <div className="w-1/4">
                  <SideLabelInput
                    id="value"
                    name="value"
                    label="Total Cost"
                    value={grossAll ?? ""}
                    onChange={handleEventChange}
                    disabled
                  />
                </div>
              </div>
              <div className="flex text-xs mb-4">
                <div className="w-1/4 mr-8">
                  <SideLabelInput
                    id="name"
                    name="name"
                    label="Venue / Place"
                    value={event.name ?? ""}
                    onChange={handleEventChange}
                  />
                </div>
                <div className="w-1/4 mr-8">
                  <SideLabelInput
                    id="notes"
                    name="notes"
                    label="For What"
                    value={event.notes ?? ""}
                    onChange={handleEventChange}
                  />
                </div>
                <div className="w-1/4 mr-8">
                  <SideLabelInput
                    id="whom"
                    name="whom"
                    label="For Whom"
                    value={event.whom ?? ""}
                    onChange={handleEventChange}
                    disabled
                  />
                </div>
              </div>
              <div className="flex text-xs">
                <div className="w-1/4 mr-8">
                  <SideLabelInput
                    id="city"
                    name="city"
                    label="City"
                    value={event.city ?? ""}
                    onChange={handleEventChange}
                  />
                </div>
                <div className="w-1/4 mr-8">
                  <div className="flex">
                    <div className="flex items-center w-1/3">
                      <label htmlFor="state" className="text-xs">
                        State
                      </label>
                    </div>
                    <div className="w-full ml-5">
                      <Select
                        id="state"
                        name="state"
                        placeholder="Select state"
                        options={stateOptions}
                        onChange={(option, name) => handleSelect(option, name)}
                        className="text-xs font-light font-light rounded-md justify-self-end"
                        value={
                          stateOptions?.filter(
                            (each) => each.value === event.state
                          ) ?? ""
                        }
                      />
                    </div>
                  </div>
                </div>
                <div className="w-1/4">
                  <SideLabelInput
                    id="zip_code"
                    name="zip_code"
                    label="Zip Code"
                    value={event.zip_code ?? ""}
                    onChange={handleEventChange}
                  />
                </div>
              </div>
            </div>
          </div>
          {prodNamesArray.length > 0 ? (
            <div>
              <form className="-mb-4">{renderTable()}</form>
              {(Object.keys(alterProdsObj).length > 0 || showButtons) && (
                <span className="flex justify-center mt-16">
                  <span className="mr-2">
                    <Button
                      className="button button-white"
                      onClick={revert}
                      text="Revert"
                      disabled={isFetching}
                    ></Button>
                  </span>
                  <Button
                    className="button"
                    onClick={handleUpdateSales}
                    text="Update"
                    disabled={isFetching}
                  ></Button>
                </span>
              )}
              {errorMessage?.error && (
                <div className="text-red-500 flex justify-center my-4">
                  {errorMessage.error}
                </div>
              )}
            </div>
          ) : (
            <div className="mx-4 my-12">
              No products have been added for this artist yet.
            </div>
          )}
        </div>
      </div>
      {productNames.length > 0 && renderFooter()}
    </SalesLayout>
  );
};

export default CompDetails;
